<template>
  <div class="password-reset-container">
    <!-- Vista 1: Solicitud de restablecimiento (pedir email) -->
    <div v-if="currentStep === 'request'" class="form-container">
      <h3 class="mb-3">Recuperar contraseña</h3>
      <p class="text-muted mb-4">Ingresa tu correo electrónico y te enviaremos un código de verificación para restablecer tu contraseña.</p>
      
      <div class="alert alert-danger" v-if="errorMessage">
        {{ errorMessage }}
      </div>
      
      <form @submit.prevent="requestReset">
        <div class="form-group mb-3">
          <label for="email">Correo electrónico</label>
          <input 
            type="email" 
            id="email" 
            class="form-control" 
            v-model="email"
            :class="{ 'is-invalid': v$.email.$error }"
            placeholder="Ingresa tu correo electrónico"
            required
          />
          <div v-if="v$.email.$error" class="invalid-feedback">
            Por favor ingresa un correo electrónico válido.
          </div>
        </div>
        
        <div class="d-grid gap-2">
          <button 
            type="submit" 
            class="btn btn-primary btn-block" 
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Enviar código
          </button>
          <button 
            type="button" 
            class="btn btn-outline-secondary btn-block"
            @click="goBackToLogin"
          >
            Volver al inicio de sesión
          </button>
        </div>
      </form>
    </div>
    
    <!-- Vista 2: Verificación de código y nueva contraseña -->
    <div v-if="currentStep === 'verify'" class="form-container">
      <h3 class="mb-3">Establece tu nueva contraseña</h3>
      <p class="text-muted mb-4">Ingresa el código de verificación que enviamos a tu correo electrónico y establece tu nueva contraseña.</p>
      
      <div class="alert alert-danger" v-if="errorMessage">
        {{ errorMessage }}
      </div>
      
      <form @submit.prevent="verifyAndReset">
        <div class="form-group mb-3">
          <label for="verificationCode">Código de verificación</label>
          <input 
            type="text" 
            id="verificationCode" 
            class="form-control text-center"
            v-model="verificationCode"
            :class="{ 'is-invalid': v$.verificationCode.$error }"
            placeholder="Ingresa el código de 6 dígitos"
            maxlength="6"
            required
          />
          <div v-if="v$.verificationCode.$error" class="invalid-feedback">
            El código debe tener 6 dígitos.
          </div>
        </div>
        
        <div class="form-group mb-3">
          <label for="newPassword">Nueva contraseña</label>
          <div class="position-relative">
            <input 
              :type="showPassword ? 'text' : 'password'" 
              id="newPassword" 
              class="form-control" 
              v-model="newPassword"
              :class="{ 'is-invalid': v$.newPassword.$error }"
              placeholder="Ingresa tu nueva contraseña"
              required
            />
            <span 
              class="password-toggle" 
              @click="showPassword = !showPassword"
            >
              <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </span>
          </div>
          <div v-if="v$.newPassword.$error" class="invalid-feedback">
            La contraseña debe tener al menos 6 caracteres.
          </div>
        </div>
        
        <div class="form-group mb-3">
          <label for="confirmPassword">Confirmar contraseña</label>
          <div class="position-relative">
            <input 
              :type="showPassword ? 'text' : 'password'" 
              id="confirmPassword" 
              class="form-control" 
              v-model="confirmPassword"
              :class="{ 'is-invalid': v$.confirmPassword.$error }"
              placeholder="Confirma tu nueva contraseña"
              required
            />
          </div>
          <div v-if="v$.confirmPassword.$error" class="invalid-feedback">
            Las contraseñas no coinciden.
          </div>
        </div>
        
        <div class="d-grid gap-2">
          <button 
            type="submit" 
            class="btn btn-primary btn-block" 
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Restablecer contraseña
          </button>
          <button 
            type="button" 
            class="btn btn-link btn-block"
            @click="currentStep = 'request'"
          >
            Volver al paso anterior
          </button>
        </div>
      </form>
    </div>
    
    <!-- Vista 3: Éxito -->
    <div v-if="currentStep === 'success'" class="form-container text-center">
      <div class="mb-4">
        <i class="bi bi-check-circle-fill text-success" style="font-size: 3rem;"></i>
      </div>
      <h3 class="mb-3">¡Contraseña restablecida!</h3>
      <p class="mb-4">Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión con tu nueva contraseña.</p>
      
      <div class="d-grid">
        <button 
          class="btn btn-primary btn-block" 
          @click="goBackToLogin"
        >
          Ir a inicio de sesión
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email as emailValidator, minLength, sameAs } from '@vuelidate/validators'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { requestPasswordReset, verifyPasswordReset } from '@/services/passwordReset'

const router = useRouter()
const toast = useToast()

// Estado del formulario
const email = ref('')
const verificationCode = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

// Estado de UI
const currentStep = ref('request')
const isLoading = ref(false)
const errorMessage = ref('')
const showPassword = ref(false)

// Validaciones
const rules = computed(() => {
  return {
    email: { required, email: emailValidator },
    verificationCode: { required, minLength: minLength(6) },
    newPassword: { required, minLength: minLength(6) },
    confirmPassword: { 
      required,
      sameAsPassword: sameAs(newPassword.value)
    }
  }
})

const v$ = useVuelidate(rules, {
  email,
  verificationCode,
  newPassword,
  confirmPassword
})

// Método para solicitar el restablecimiento
const requestReset = async () => {
  try {
    errorMessage.value = ''
    const isEmailValid = await v$.value.email.$validate()
    if (!isEmailValid) return
    
    isLoading.value = true
    
    await requestPasswordReset(email.value)
    
    toast.success('Te hemos enviado un código de verificación a tu correo electrónico')
    currentStep.value = 'verify'
  } catch (error) {
    errorMessage.value = error.response?.data?.error || 'Error al procesar la solicitud'
    toast.error(errorMessage.value)
  } finally {
    isLoading.value = false
  }
}

// Método para verificar y restablecer
const verifyAndReset = async () => {
  try {
    errorMessage.value = ''
    const isFormValid = await v$.value.$validate()
    if (!isFormValid) return
    
    isLoading.value = true
    
    const response = await verifyPasswordReset({
      email: email.value,
      code: verificationCode.value,
      new_password: newPassword.value
    })
    
    // Guardar el token de acceso
    if (response.access) {
      localStorage.setItem('accessToken', response.access)
    }
    
    toast.success('¡Contraseña actualizada correctamente!')
    currentStep.value = 'success'
  } catch (error) {
    errorMessage.value = error.response?.data?.error || 'Error al verificar el código'
    toast.error(errorMessage.value)
  } finally {
    isLoading.value = false
  }
}

// Método para volver al login
const goBackToLogin = () => {
  router.push('/iniciar-sesion')
}
</script>

<style scoped>
.password-reset-container {
  width: 100%;
}

.form-container {
  background-color: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  max-width: 450px;
  margin: 0 auto;
}

.password-toggle {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #6c757d;
  transition: color 0.2s ease;
  z-index: 10;
}

.password-toggle:hover {
  color: #4a90e2;
}
</style>